import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  AI_REPORT_TYPES,
  AiReport,
  AiReportListItem,
  AiReportParams,
  AiReportType,
  AiReportsService,
} from 'src/app/shared/services/ai-reports.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { AiReportFormComponent } from '../ai-report-form/ai-report-form.component';
import { AiReportViewComponent } from '../ai-report-view/ai-report-view.component';

export interface AiReportDialogData {
  type: AiReportType;
  preset?: Partial<AiReportParams>;
  reportId?: number;
}

@Component({
  selector: 'app-ai-report-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, AiReportFormComponent, AiReportViewComponent],
  templateUrl: './ai-report-dialog.component.html',
  styleUrls: ['./ai-report-dialog.component.css'],
})
export class AiReportDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  report: AiReport | null = null;
  recent: AiReportListItem[] = [];
  generating = false;
  loading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AiReportDialogData,
    private dialogRef: MatDialogRef<AiReportDialogComponent>,
    private reports: AiReportsService,
    private toastr: ToastrMsgService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.data.reportId) {
      this.open(this.data.reportId);
    } else {
      this.loadRecent();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get typeLabel(): string {
    return AI_REPORT_TYPES.find((t) => t.type === this.data.type)?.label || 'AI report';
  }

  get projectLocked(): boolean {
    return !!this.data.preset?.projectId;
  }

  loadRecent(): void {
    if (!this.data.preset?.projectId) return;
    this.reports
      .list({ type: this.data.type, projectId: this.data.preset.projectId, limit: 5 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (rows) => (this.recent = rows), error: () => (this.recent = []) });
  }

  generate(params: AiReportParams): void {
    this.generating = true;
    this.reports
      .generate(params)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.generating = false)))
      .subscribe({
        next: (report) => (this.report = report),
        error: (err) => this.toastr.error(err?.error?.message || 'Failed to generate report'),
      });
  }

  regenerate(): void {
    if (!this.report) return;
    this.generate({ ...this.reports.paramsFromReport(this.report), useAi: true });
  }

  open(id: number): void {
    this.loading = true;
    this.reports
      .get(id)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loading = false)))
      .subscribe({
        next: (report) => (this.report = report),
        error: () => this.toastr.error('Report not found'),
      });
  }

  back(): void {
    this.report = null;
    this.loadRecent();
  }

  openHub(): void {
    const p = this.data.preset || {};
    const queryParams: Record<string, any> = { type: this.data.type };
    if (p.projectId) queryParams['projectId'] = p.projectId;
    if (p.boqId) queryParams['boqId'] = p.boqId;
    if (p.bomId) queryParams['bomId'] = p.bomId;
    if (p.date) queryParams['date'] = p.date;
    if (p.from) queryParams['from'] = p.from;
    if (p.to) queryParams['to'] = p.to;
    if (this.report) queryParams['reportId'] = this.report.id;
    this.dialogRef.close();
    this.router.navigate(['/home/ai-reports'], { queryParams });
  }

  close(): void {
    this.dialogRef.close(this.report);
  }
}
